export type AppChromeMode = 'chromeless' | 'floating' | 'embedded-scheduling' | 'workspace';
export type AppDockMode = 'global' | 'hidden';
export type MobileNavigationLayout = 'fixed' | 'inset' | 'hidden';
export type RouteFamilyId =
  | 'core'
  | 'catalog'
  | 'scheduling'
  | 'planning'
  | 'campus-life'
  | 'campus-utility'
  | 'assistant'
  | 'account'
  | 'auth';
export type PageArchetype = 'standard' | 'directory' | 'wide' | 'workspace' | 'spatial' | 'reading';
export type PageDensity = 'comfortable' | 'compact';
export type ScrollOwner = 'document' | 'workspace' | 'map' | 'none';
export type CanvasKind = 'neutral' | 'landing' | 'workspace' | 'spatial';

export type RouteLayoutContract = {
  pageArchetype: PageArchetype;
  pageDensity: PageDensity;
  scrollOwner: ScrollOwner;
  canvas: CanvasKind;
};

export type RoutePresentationDataAttributes = {
  'data-ss-page-archetype': PageArchetype;
  'data-ss-density': PageDensity;
  'data-ss-scroll-owner': ScrollOwner;
  'data-ss-canvas': CanvasKind;
};

export type RoutePresentation = {
  family: RouteFamilyId;
  chromeMode: AppChromeMode;
  dockMode: AppDockMode;
  mobileNavigation: MobileNavigationLayout;
  launcherIconClassName: string;
  pageArchetype: PageArchetype;
  pageDensity: PageDensity;
  scrollOwner: ScrollOwner;
  canvas: CanvasKind;
};

export type RouteFamilyDefinition = {
  id: RouteFamilyId;
  name: string;
  routes: string[];
  palette: string[];
  notes: string;
  presentation: RoutePresentation;
};

const ICON_TREATMENTS: Record<RouteFamilyId, string> = {
  core: 'border-slate-300/80 border-l-4 border-l-slate-500 bg-slate-50 text-slate-700 dark:border-white/14 dark:border-l-slate-400 dark:bg-slate-800/80 dark:text-slate-100',
  catalog: 'border-slate-300/80 border-l-4 border-l-sky-600 bg-sky-50/70 text-slate-700 dark:border-white/14 dark:border-l-sky-400 dark:bg-slate-800/80 dark:text-slate-100',
  scheduling: 'border-slate-300/80 border-l-4 border-l-violet-600 bg-violet-50/70 text-slate-700 dark:border-white/14 dark:border-l-violet-400 dark:bg-slate-800/80 dark:text-slate-100',
  planning: 'border-slate-300/80 border-l-4 border-l-emerald-600 bg-emerald-50/70 text-slate-700 dark:border-white/14 dark:border-l-emerald-400 dark:bg-slate-800/80 dark:text-slate-100',
  'campus-life': 'border-slate-300/80 border-l-4 border-l-amber-600 bg-amber-50/70 text-slate-700 dark:border-white/14 dark:border-l-amber-400 dark:bg-slate-800/80 dark:text-slate-100',
  'campus-utility': 'border-slate-300/80 border-l-4 border-l-sky-600 bg-sky-50/70 text-slate-700 dark:border-white/14 dark:border-l-sky-400 dark:bg-slate-800/80 dark:text-slate-100',
  assistant: 'border-slate-300/80 border-l-4 border-l-indigo-600 bg-indigo-50/70 text-slate-700 dark:border-white/14 dark:border-l-indigo-400 dark:bg-slate-800/80 dark:text-slate-100',
  account: 'border-slate-300/80 border-l-4 border-l-rose-600 bg-rose-50/70 text-slate-700 dark:border-white/14 dark:border-l-rose-400 dark:bg-slate-800/80 dark:text-slate-100',
  auth: 'border-slate-300/80 bg-slate-100 text-slate-700 dark:border-white/14 dark:bg-slate-800 dark:text-slate-100',
};

function floating(
  family: RouteFamilyId,
  dockMode: AppDockMode = 'global',
  mobileNavigation: MobileNavigationLayout = 'fixed',
  layout: Partial<RouteLayoutContract> = {},
): RoutePresentation {
  return {
    family,
    chromeMode: 'floating',
    dockMode,
    mobileNavigation,
    launcherIconClassName: ICON_TREATMENTS[family],
    pageArchetype: layout.pageArchetype ?? 'standard',
    pageDensity: layout.pageDensity ?? 'comfortable',
    scrollOwner: layout.scrollOwner ?? 'document',
    canvas: layout.canvas ?? 'neutral',
  };
}

export const ROUTE_FAMILIES: RouteFamilyDefinition[] = [
  {
    id: 'core', name: 'Core and public', routes: ['/', '/help', '/support', '/admin', '/apply', '/developers', '/design-system', '/contact', '/privacy-policy', '/tos', '/sirs'], palette: ['#CC0033', '#FFF1F3', '#F8FAFC', '#0F172A'], notes: 'Product home, maintained references, support, and public utility surfaces.',
    presentation: floating('core'),
  },
  {
    id: 'catalog', name: 'Neutral catalog', routes: ['/explore', '/courses', '/professors', '/course-list'], palette: ['#F8FAFC', '#FFFFFF', '#E2E8F0', '#0F172A'], notes: 'Quiet, solid academic reading and comparison surfaces.',
    presentation: floating('catalog', 'global', 'fixed', { pageArchetype: 'directory' }),
  },
  {
    id: 'scheduling', name: 'Scheduling tools', routes: ['/scheduling', '/finals', '/course-lists', '/course-sniper', '/classes-by-time', '/classes-on-map'], palette: ['#7C3AED', '#EDE9FE', '#F8FAFC', '#0F172A'], notes: 'A restrained violet rail identifies dense section controls and timetable decisions.',
    presentation: floating('scheduling', 'global', 'fixed', { pageArchetype: 'directory' }),
  },
  {
    id: 'planning', name: 'Academic planning', routes: ['/planning', '/degree-planning', '/degree-search', '/prerequisite-checker', '/gpa-calculator'], palette: ['#059669', '#ECFDF5', '#F8FAFC', '#0F172A'], notes: 'A restrained emerald rail identifies requirements and plan actions.',
    presentation: floating('planning'),
  },
  {
    id: 'campus-life', name: 'Campus life', routes: ['/events', '/dining', '/parking', '/research-opportunities', '/research-outreach', '/resources', '/cold-email'], palette: ['#D97706', '#FFFBEB', '#F8FAFC', '#0F172A'], notes: 'A restrained amber rail identifies feeds, campus services, and outreach.',
    presentation: floating('campus-life', 'global', 'fixed', { pageArchetype: 'directory' }),
  },
  {
    id: 'campus-utility', name: 'Campus utilities', routes: ['/campus', '/map', '/parking-map', '/bus-tracker', '/room-availability', '/info-rutgers'], palette: ['#0284C7', '#F0F9FF', '#F8FAFC', '#0F172A'], notes: 'A restrained sky rail identifies maps, live status, and utility data.',
    presentation: floating('campus-utility'),
  },
  {
    id: 'assistant', name: 'Assistant workspace', routes: ['/ai-assistant', '/ontology'], palette: ['#64748B', '#CBD5E1', '#F7F7F6', '#0B0F17'], notes: 'Dedicated full-height workspaces with solid decision panels.',
    presentation: floating('assistant', 'global', 'fixed', { pageArchetype: 'workspace', pageDensity: 'compact', scrollOwner: 'workspace', canvas: 'workspace' }),
  },
  {
    id: 'account', name: 'Account and community', routes: ['/profile', '/friends', '/class-chat'], palette: ['#E11D48', '#FFF1F2', '#F8FAFC', '#0F172A'], notes: 'A restrained rose rail identifies personal data, connections, and community context.',
    presentation: floating('account'),
  },
  {
    id: 'auth', name: 'Authentication and callbacks', routes: ['/login', '/onboarding', '/forgot-password', '/reset-password', '/auth', '/oauth/authorize', '/oauth/google-callback'], palette: ['#FFFFFF', '#E2E8F0', '#475569', '#030712'], notes: 'Focused entry and callback routes that intentionally suppress product chrome.',
    presentation: {
      ...floating('auth', 'hidden', 'hidden'),
      chromeMode: 'chromeless',
    },
  },
];

const CHROMELESS_PREFIXES = ['/login', '/onboarding', '/forgot-password', '/reset-password', '/auth', '/oauth/authorize', '/oauth/google-callback', '/dashboard1', '/dashboard2', '/dashboard3'];

const DEFAULT_PRESENTATION: RoutePresentation = floating('core');
const EMBEDDED_SCHEDULING_PRESENTATION: RoutePresentation = {
  ...floating('scheduling', 'global', 'hidden', {
    pageArchetype: 'workspace', pageDensity: 'compact', scrollOwner: 'workspace', canvas: 'workspace',
  }),
  chromeMode: 'embedded-scheduling',
};
const INFO_RUTGERS_PRESENTATION: RoutePresentation = {
  ...floating('campus-utility', 'hidden', 'hidden', {
    pageArchetype: 'workspace', pageDensity: 'compact', scrollOwner: 'workspace', canvas: 'workspace',
  }),
  chromeMode: 'workspace',
};
const EXPLORE_PRESENTATION: RoutePresentation = {
  ...ROUTE_FAMILIES.find((family) => family.id === 'catalog')!.presentation,
  dockMode: 'global',
  mobileNavigation: 'inset',
};
const OPTIMAL_SETUP_PRESENTATION: RoutePresentation = floating('core', 'hidden', 'inset');
const PROFESSOR_WIKI_PRESENTATION: RoutePresentation = floating('catalog', 'global', 'inset');
const COURSE_SNIPER_ALERT_PRESENTATION: RoutePresentation = floating('scheduling', 'hidden', 'inset');

const ROUTE_OVERRIDES: Array<{ prefix: string; presentation: RoutePresentation }> = [
  { prefix: '/planning', presentation: floating('planning', 'global', 'inset', { pageArchetype: 'wide', pageDensity: 'compact' }) },
  { prefix: '/degree-search', presentation: floating('planning', 'global', 'fixed', { pageArchetype: 'wide', pageDensity: 'compact' }) },
  { prefix: '/degree-planning', presentation: floating('planning', 'hidden', 'fixed', { pageArchetype: 'workspace', pageDensity: 'compact', scrollOwner: 'document' }) },
  { prefix: '/ontology', presentation: { ...floating('assistant', 'hidden', 'hidden', { pageArchetype: 'spatial', pageDensity: 'compact', scrollOwner: 'map', canvas: 'spatial' }), chromeMode: 'workspace' } },
  { prefix: '/classes-on-map', presentation: floating('scheduling', 'global', 'fixed', { pageArchetype: 'spatial', pageDensity: 'compact', scrollOwner: 'map', canvas: 'spatial' }) },
  { prefix: '/map', presentation: floating('campus-utility', 'global', 'fixed', { pageArchetype: 'spatial', pageDensity: 'compact', scrollOwner: 'map', canvas: 'spatial' }) },
  { prefix: '/parking-map', presentation: floating('campus-utility', 'global', 'fixed', { pageArchetype: 'spatial', pageDensity: 'compact', scrollOwner: 'map', canvas: 'spatial' }) },
  { prefix: '/bus-tracker', presentation: floating('campus-utility', 'global', 'fixed', { pageArchetype: 'spatial', pageDensity: 'compact', scrollOwner: 'map', canvas: 'spatial' }) },
  { prefix: '/room-availability/map', presentation: floating('campus-utility', 'global', 'fixed', { pageArchetype: 'spatial', pageDensity: 'compact', scrollOwner: 'map', canvas: 'spatial' }) },
  { prefix: '/room-availability', presentation: floating('campus-utility', 'global', 'fixed', { pageArchetype: 'directory' }) },
  { prefix: '/rooms', presentation: floating('campus-utility', 'global', 'fixed', { pageArchetype: 'directory' }) },
  { prefix: '/support', presentation: floating('core', 'global', 'fixed', { pageArchetype: 'reading' }) },
  { prefix: '/help', presentation: floating('core', 'global', 'fixed', { pageArchetype: 'wide', pageDensity: 'compact' }) },
  { prefix: '/admin', presentation: floating('core', 'hidden', 'fixed', { pageArchetype: 'wide', pageDensity: 'compact', scrollOwner: 'document' }) },
  { prefix: '/apply', presentation: floating('core') },
  { prefix: '/developers', presentation: floating('core', 'global', 'fixed', { pageArchetype: 'reading' }) },
  { prefix: '/design-system', presentation: floating('core') },
  { prefix: '/optimal-setup', presentation: OPTIMAL_SETUP_PRESENTATION },
  { prefix: '/professors', presentation: PROFESSOR_WIKI_PRESENTATION },
  { prefix: '/course-sniper/alert-fixtures', presentation: COURSE_SNIPER_ALERT_PRESENTATION },
  { prefix: '/course-sniper/alert', presentation: COURSE_SNIPER_ALERT_PRESENTATION },
  { prefix: '/course-sniper', presentation: floating('scheduling', 'global', 'fixed', { pageArchetype: 'workspace', pageDensity: 'compact', scrollOwner: 'workspace' }) },
  { prefix: '/scheduling/view', presentation: floating('scheduling', 'hidden', 'inset', { pageArchetype: 'workspace', pageDensity: 'compact', scrollOwner: 'document' }) },
  { prefix: '/prerequisite-checker', presentation: floating('planning', 'global', 'fixed', { pageArchetype: 'workspace', pageDensity: 'compact', scrollOwner: 'document' }) },
  { prefix: '/gpa-calculator', presentation: floating('planning', 'global', 'fixed', { pageArchetype: 'wide', pageDensity: 'compact' }) },
  { prefix: '/class-chat', presentation: floating('account', 'global', 'fixed', { pageArchetype: 'workspace', pageDensity: 'compact', scrollOwner: 'document' }) },
  { prefix: '/cold-email', presentation: floating('campus-life', 'global', 'fixed', { pageArchetype: 'workspace', pageDensity: 'compact', scrollOwner: 'document' }) },
  { prefix: '/research-outreach', presentation: floating('campus-life', 'global', 'fixed', { pageArchetype: 'directory', pageDensity: 'compact', scrollOwner: 'document' }) },
  { prefix: '/guides', presentation: floating('core', 'global', 'fixed', { pageArchetype: 'reading' }) },
  { prefix: '/sources', presentation: floating('core', 'global', 'fixed', { pageArchetype: 'reading' }) },
  { prefix: '/privacy-policy', presentation: floating('core', 'global', 'fixed', { pageArchetype: 'reading' }) },
  { prefix: '/tos', presentation: floating('core', 'global', 'fixed', { pageArchetype: 'reading' }) },
  { prefix: '/sirs', presentation: floating('core', 'hidden', 'inset', { pageArchetype: 'directory' }) },
  { prefix: '/friends', presentation: floating('account', 'global', 'fixed', { pageArchetype: 'directory' }) },
  { prefix: '/users', presentation: floating('core', 'global', 'fixed', { scrollOwner: 'none' }) },
  // The landing page uses a calm, solid hero canvas. matchesPrefix treats '/'
  // as exact, so this cannot swallow other routes.
  { prefix: '/', presentation: floating('core', 'global', 'fixed', { canvas: 'landing' }) },
];

/** The assistant owns the full workspace; global launchers stay out of its transcript and composer. */
const AI_ASSISTANT_PRESENTATION: RoutePresentation = floating('assistant', 'hidden', 'hidden', {
  pageArchetype: 'workspace', pageDensity: 'compact', scrollOwner: 'workspace', canvas: 'workspace',
});

function matchesPrefix(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function getRoutePresentation(pathname: string): RoutePresentation {
  if (CHROMELESS_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix))) {
    const isRedirect = matchesPrefix(pathname, '/auth/callback') || matchesPrefix(pathname, '/oauth/google-callback');
    const isOnboarding = matchesPrefix(pathname, '/onboarding');
    return {
      ...floating('auth', 'hidden', 'hidden', isRedirect
        ? { scrollOwner: 'none' }
        : isOnboarding
          ? { pageArchetype: 'standard', pageDensity: 'comfortable', scrollOwner: 'document' }
          : {}),
      chromeMode: 'chromeless',
    };
  }
  if (pathname === '/scheduling') return EMBEDDED_SCHEDULING_PRESENTATION;
  if (matchesPrefix(pathname, '/info-rutgers')) return INFO_RUTGERS_PRESENTATION;
  if (matchesPrefix(pathname, '/explore')) return EXPLORE_PRESENTATION;
  const override = ROUTE_OVERRIDES.find(({ prefix }) => matchesPrefix(pathname, prefix));
  if (override) return override.presentation;

  for (const family of ROUTE_FAMILIES) {
    if (family.id === 'assistant') continue;
    if (family.routes.some((route) => matchesPrefix(pathname, route))) return family.presentation;
  }
  if (matchesPrefix(pathname, '/ai-assistant')) return AI_ASSISTANT_PRESENTATION;
  if (matchesPrefix(pathname, '/ontology')) return ROUTE_OVERRIDES.find(({ prefix }) => prefix === '/ontology')!.presentation;
  return DEFAULT_PRESENTATION;
}

export function getDesignRouteFamilies() {
  return ROUTE_FAMILIES.map(({ id, name, routes, palette, notes }) => ({ id, name, routes, palette, notes }));
}

export function getRouteFamily(pathname: string): RouteFamilyId {
  return getRoutePresentation(pathname).family;
}

/** Stable DOM metadata for shell, audit, and specimen boundaries. */
export function getRoutePresentationDataAttributes(
  presentation: RouteLayoutContract,
): RoutePresentationDataAttributes {
  return {
    'data-ss-page-archetype': presentation.pageArchetype,
    'data-ss-density': presentation.pageDensity,
    'data-ss-scroll-owner': presentation.scrollOwner,
    'data-ss-canvas': presentation.canvas,
  };
}

/** Overflow is owned only by the typed route contract, never theme or chrome. */
export function getScrollOwnerOverflowClassName(scrollOwner: ScrollOwner): string {
  return scrollOwner === 'document'
    ? 'overflow-x-hidden overflow-y-auto'
    : 'overflow-hidden';
}
